import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  BellIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  EllipsisVerticalIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import DropdownMenuPortal from "../ui/DropdownMenuPortal";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNotificationContext } from "../contexts/NotificationContext";

const STORAGE_KEY = "traces:sidebar:expanded";
const HOVER_STORAGE_KEY = "traces:sidebar:hover";

// -------------------- Helpers --------------------
/**
 * Detects if user has disabled animations in their operating systems
 */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(!!m.matches);
    onChange();
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

function initialsFromName(name = "") {
  if (!name) return "T"; // default
  /**
   * Trimming white spaces
   * Splitting string by whitespace characters (tabs, spaces, newlines)
   * Taking only the first 2 elements ()
   */
  const parts = name.trim().split(/\s+/).slice(0, 2);
  /**
   * Taking 1st char & Capitalizing it
   * Joining all the chars into one string
   * If all fails, fall back to returning "T"
   */
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "T";
}

function isActive(href, activePath) {
  if (activePath)
    return activePath === href || activePath.startsWith(href + "/");
  if (typeof window !== "undefined") {
    const path = window.location?.pathname || "/";
    return path === href || path.startsWith(href + "/");
  }
  return false;
}

// -------------------- Icons Config --------------------
const NAV_ITEMS = [
  { key: "home", label: "Home", icon: HomeIcon, href: "/home" },
  {
    key: "notes",
    label: "Notes",
    icon: ClipboardDocumentListIcon,
    href: "/notes", // Updated to match your route
  },
  {
    key: "notifications",
    label: "Notifications",
    icon: BellIcon,
    href: "/notifications",
    // Badge count is dynamic - set in component based on unread notifications
  },
  // {
  //   key: "messages",
  //   label: "Messages",
  //   icon: EnvelopeIcon,
  //   href: "/messages",
  //   badge: 10,
  // },
  {
    key: "settings",
    label: "Settings",
    icon: Cog6ToothIcon,
    href: "/settings",
  },
];

// -------------------- Logo --------------------
function LogoMark({ collapsed, hover }) {
  const showChevron = collapsed && hover;

  return (
    <div
      className={[
        "relative grid place-items-center h-9 w-9 rounded-xl bg-surface-elevated",
        showChevron ? "border border-border" : "",
      ].join(" ")}
      aria-hidden="false"
    >
      {/* Chevron icon - fades in when collapsed and hovered */}
      <ChevronRightIcon
        className={[
          "absolute h-5 w-5 text-text-muted transition-opacity duration-150",
          showChevron ? "opacity-100" : "opacity-0",
        ].join(" ")}
        aria-hidden="false"
      />

      {/* T logo - fades out when collapsed and hovered */}
      <span
        className={[
          "absolute font-black text-2xl select-none transition-opacity duration-150 text-primary",
          showChevron ? "opacity-0" : "opacity-100",
        ].join(" ")}
      >
        T
      </span>
    </div>
  );
}

function LogoWordmark() {
  return (
    <span className="ml-1 text-xl font-extrabold tracking-tight text-text-primary">
      Traces
    </span>
  );
}

// -------------------- Sidebar Component --------------------
export default function Sidebar({
  activePath,
  onLogout = () => {},
  onNavigate,
}) {
  /**
   * Get current user from auth context
   * This will work for both mock and real backend
   */
  const { user } = useAuth();

  /**
   * Get unread notification count for badge
   * Now managed by NotificationContext with automatic polling
   */
  const { unreadCount } = useNotificationContext();

  /**
   * State returned from hook
   * used to see if user has reduced Motion enabled on their OS
   */
  const reduced = usePrefersReducedMotion();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  /**
   * Tracks the state of the sidebar: Collapsed or extended
   * Safe to read synchronously - guaranteed to exist in index.js
   */
  const [expanded, setExpanded] = useState(() => {
    if (typeof window === "undefined") return true; // SSR fallback
    return localStorage.getItem(STORAGE_KEY) === "true";
  });

  /**
   * Tracks hover detection over the sidebar
   * Safe to read synchronously - guaranteed to exist in index.js
   */
  const [hover, setHover] = useState(() => {
    if (typeof window === "undefined") return false; // SSR fallback
    const savedHover = localStorage.getItem(HOVER_STORAGE_KEY);
    return savedHover === "true";
  });

  /**
   * Persist expanded state changes to local storage
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY, String(expanded));
  }, [expanded]);

  /**
   * Persist hover state changes to local storage
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(HOVER_STORAGE_KEY, String(hover));
  }, [hover]);

  /**
   * reference to the sidebar component (whole container)
   */
  const sidebarRef = useRef(null);

  /**
   * Reference to the user card ellipsis
   */
  const userMenuEllipsisRef = useRef(null);

  // -------------------- Roving tableindex --------------------
  /* A standard accessibility pattern that allows users to navigate through sidebar items using keys */

  /**
   * Stores references to each nav item
   */
  const itemRefs = useRef([]);

  /**
   * Tracks which nav item has focus
   */
  const [focusIndex, setFocusIndex] = useState(0);

  /**
   * Function to move browser focus to specific nav item by index
   */
  const focusItem = useCallback((idx) => {
    const el = itemRefs.current[idx];
    if (el) el.focus();
  }, []);

  /**
   * Key down handler for the unordered List element containing the nav items
   */
  const onListKeyDown = useCallback(
    (e) => {
      const max = NAV_ITEMS.length - 1;
      if (e.key === "ArrowDown") {
        // Prevents browser's default behavior (ex: scrolling, etc...)
        e.preventDefault();
        // Moves focus to next item wrapping around when on last item
        const next = focusIndex >= max ? 0 : focusIndex + 1;
        setFocusIndex(next);
        focusItem(next);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        // Moves focus to previous item wrapping around when on first item
        const prev = focusIndex <= 0 ? max : focusIndex - 1;
        setFocusIndex(prev);
        focusItem(prev);
      } else if (e.key === "Home") {
        e.preventDefault();
        setFocusIndex(0);
        focusItem(0);
      } else if (e.key === "End") {
        e.preventDefault();
        setFocusIndex(max);
        focusItem(max);
      }
    },
    [focusIndex, focusItem],
  );

  const handleNavItemClick = (e, href) => {
    /**
     * Prevents default browser navigation
     * We want to handle the navigation ourselves
     */
    e.preventDefault();
    onNavigate(href);
  };

  const userMenuRef = useRef(null);

  /**
   * User profile card toggling
   */
  const toggleUserMenu = useCallback((e) => {
    /* Handling keyboard events */
    if (e?.type === "keydown" && !["Enter", " "].includes(e.key)) {
      return;
    }
    setUserMenuOpen((prev) => !prev);
  }, []);

  /**
   * close user menu handler
   */
  const closeUserMenu = useCallback(() => {
    setUserMenuOpen(false);
  }, []);

  /**
   * Runs on mount and when userMenu or closeUserMenu change
   * Adding a mousedown event listener
   *  detecting if event is outside of user menu so we can close it
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target) &&
        !userMenuEllipsisRef.current.contains(event.target)
      ) {
        closeUserMenu();
      }
    };

    if (userMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [userMenuOpen, closeUserMenu]);

  /**
   * Runs on mount and when userMenu or closeUserMenu change
   * Adding an escape keydown event listener to close the menu
   */
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && userMenuOpen) {
        closeUserMenu();
      }
    };

    if (userMenuOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [userMenuOpen, closeUserMenu]);

  const widthClass = useMemo(() => {
    if (expanded) return "w-64 md:w-72 lg:w-80";
    /**
     * Hardcoding the pixels for animation purposes.
     * Ideal class would be w-18, but w-18 is not included in tailwind's default scale
     */
    return "w-[72px]";
  }, [expanded]);

  /**
   * If user has motion reduced activated on their OS,
   * we default to no tansition animation
   */
  const transitionClass = reduced
    ? "transition-none"
    : "transition-[width] duration-75 ease-out";

  /* ----------------- Profile Info ------------------ */
  const displayName = user?.name;
  const initials = useMemo(() => initialsFromName(displayName), [displayName]);
  const email = user?.email;

  const navigate = useNavigate();

  const userMenuItems = [
    /* Add other Menu items*/

    // {
    //   label: "Profile",
    //   icon: <UserCircleIcon className="h-4 w-4 text-black" />,
    //   onSelect: () => console.log("Profile selected"),
    // },
    {
      label: "Log out",
      icon: <ArrowRightOnRectangleIcon className="h-4 w-4 text-destructive" />,
      onSelect: onLogout,
    },
  ];

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      ref={sidebarRef}
      className="hidden md:block fixed inset-y-0 left-0 z-40 pointer-events-none"
    >
      <nav
        /**
         * aria - accessible rich internet application
         * aria-label'"Primary" will read "Primary Navigation"
         */
        aria-label="Primary"
        className={[
          "relative h-screen shrink-0 pointer-events-auto",
          widthClass,
          transitionClass,
          "border border-border bg-surface-elevated shadow-xl overflow-hidden",
          "flex flex-col",
        ].join(" ")}
      >
        {/* Top: Logo/Toggler */}
        <div className="px-2 py-7">
          <div className="w-full flex items-center gap-3 p-2 rounded-2xl">
            {/* This button contains only the LogoMark */}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              /**
               * aria-pressed: screen readers will read "pressed/notpressed"
               * aria-expanded: screen readers will read "expanded/collapsed"
               */
              aria-pressed={expanded ? "true" : "false"}
              aria-expanded={expanded ? "true" : "false"}
              className="relative h-10 w-10 flex items-center rounded-2xl"
            >
              <LogoMark collapsed={!expanded} hover={hover} />
              <span className="sr-only">Toggle sidebar</span>
            </button>

            {/* This is the non-clickable title */}
            {expanded && <LogoWordmark />}

            {/* This is the separate button for the ChevronLeftIcon */}
            {expanded && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="ml-auto relative h-9 w-9 flex items-center justify-center rounded-xl border border-border hover:bg-surface"
              >
                <ChevronLeftIcon
                  className="h-3 w-3 text-text-muted group-hover:text-text-primary transition-colors duration-150"
                  aria-hidden="true"
                />
              </button>
            )}
          </div>
        </div>

        <div className="mx-3 border-b border-border-strong" />

        {/* Nav List */}
        <div className="flex-1 overflow-y-auto py-5 pretty-scroll">
          <ul
            className="px-2 py-3 space-y-1"
            onKeyDown={onListKeyDown}
            role="listbox"
            aria-label="Main navigation"
          >
            {NAV_ITEMS.map((item, idx) => {
              const ActiveIcon = item.icon;
              const active = isActive(item.href, activePath);
              return (
                <li key={item.key} className="relative">
                  <a
                    href={item.href}
                    ref={(el) => (itemRefs.current[idx] = el)}
                    tabIndex={idx === focusIndex ? 0 : -1}
                    onFocus={() => setFocusIndex(idx)}
                    onClick={(e) => handleNavItemClick(e, item.href)}
                    role="link"
                    aria-current={active ? "page" : undefined}
                    className={[
                      "group flex items-center gap-3 rounded-xl mx-1 px-1",
                      "min-h-[44px]",
                      active
                        ? "bg-surface-muted font-semibold"
                        : "hover:bg-surface-sunken",
                    ].join(" ")}
                  >
                    {/* Icon rail cell (fixed 56px) */}
                    <div className="relative flex-none w-[40px] grid place-items-center">
                      <ActiveIcon
                        className={[
                          "h-6 w-6",
                          active ? "text-primary" : "text-text-secondary",
                        ].join(" ")}
                        aria-hidden="true"
                      />

                      {/* Unread indicator dot for notifications when collapsed */}
                      {!expanded &&
                        item.key === "notifications" &&
                        unreadCount > 0 && (
                          <span
                            className="absolute top-0 right-0 inline-block h-2 w-2 rounded-full bg-primary ring-2 ring-surface-elevated"
                            aria-hidden="true"
                          />
                        )}

                      {!expanded && (
                        <span className="sr-only">{item.label}</span>
                      )}
                    </div>

                    {/* Label / Badge (only when expanded) */}
                    {expanded && (
                      <div className="flex items-center justify-between flex-1 px-2">
                        <span className="truncate">{item.label}</span>
                        {/* Show badge for notifications with dynamic unread count */}
                        {item.key === "notifications" && unreadCount > 0 ? (
                          <span className="ml-2 inline-flex items-center justify-center text-xs rounded-md px-1.5 h-5 bg-surface-muted text-text-secondary">
                            {unreadCount}
                          </span>
                        ) : item.badge ? (
                          <span className="ml-2 inline-flex items-center justify-center text-xs rounded-md px-1.5 h-5 bg-surface-muted text-text-secondary">
                            {item.badge}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Profile */}
        <div className="p-2 pb-6 flex">
          <div className="w-full group relative flex items-center gap-3 p-2 rounded-2xl border border-border">
            <button className="grid place-items-center h-9 w-9 rounded-full bg-primary-default border border-border text-primary font-semibold flex-shrink-0 overflow-hidden">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                initials
              )}
            </button>
            {expanded && (
              <span className="min-w-0 flex-1 pl-2">
                <div className="text-sm font-medium truncate">
                  {displayName}
                </div>
                <div className="text-xs text-text-muted truncate">{email}</div>
              </span>
            )}
            {expanded && (
              <button
                className="ml-auto rounded-2xl border border-border hover:bg-surface"
                ref={userMenuEllipsisRef}
                onClick={toggleUserMenu}
                onKeyDown={toggleUserMenu}
                aria-expanded={userMenuOpen}
                aria-label="User menu"
              >
                <EllipsisVerticalIcon className="h-5 w-5" />
              </button>
            )}
            {!expanded && <span className="sr-only">{displayName}</span>}
          </div>
        </div>
        {userMenuOpen && userMenuEllipsisRef?.current && (
          <DropdownMenuPortal
            anchorRef={userMenuEllipsisRef}
            onClose={toggleUserMenu}
            items={userMenuItems}
          />
        )}
      </nav>
    </div>
  );
}

export { NAV_ITEMS, isActive };
